import React, { useState } from "react";
import { toast } from "sonner";
import { Image as ImageIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { adminCreateProduct, adminUpdateProduct, adminUploadImage, useCategories } from "@/lib/api";

const FALLBACK_CATEGORIES = [
  "Dana", "Kuzu", "Tavuk", "Köfte", "Şarküteri",
  "Premium Kesimler", "Sakatat", "Kemik & Çorbalık", "Yan Lezzetler",
];

const Field = ({ label, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-charcoal/60">
      {label}
    </Label>
    {children}
  </div>
);
const Toggle = ({ label, v, on, testid }) => (
  <label className="flex items-center gap-2.5">
    <Switch checked={v} onCheckedChange={on} data-testid={testid} />
    <span className="font-mono text-xs uppercase tracking-wide text-charcoal/70">{label}</span>
  </label>
);
const selectCls = "h-10 w-full rounded-sm border border-input bg-background px-3 text-sm";

export default function ProductEditDialog({ state, onClose, onSaved }) {
  const isCreate = state.mode === "create";
  const p = state.product || {};
  const { data: categories = [] } = useCategories();
  const categoryNames = categories.length ? categories.map((c) => c.name) : FALLBACK_CATEGORIES;

  const [form, setForm] = useState({
    slug: p.slug || "",
    name: p.name || "",
    category: p.category || "Dana",
    short_description: p.short_description || "",
    description: p.description || "",
    image: p.image || "",
    price: p.price ?? "",
    fat: p.fat || "Orta",
    difficulty: p.difficulty || "Orta",
    tags: (p.tags || []).join(", "),
    cooking_methods: (p.cooking_methods || ["Tava"]).join(", "),
    flavor: p.flavor ?? 4,
    tenderness: p.tenderness ?? 4,
    fresh_cut: !!p.fresh_cut,
    featured: !!p.featured,
    is_premium: !!p.is_premium,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await adminUploadImage(file);
      set("image", url);
      toast.success("Görsel sunucuya yüklendi");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Görsel yüklenemedi");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    if (!form.name.trim() || (isCreate && !form.slug.trim())) {
      toast.error("Ad ve slug zorunludur");
      return;
    }
    setSaving(true);
    try {
      const tags = form.tags.split(",").map((s) => s.trim()).filter(Boolean);
      const base = {
        name: form.name,
        category: form.category,
        short_description: form.short_description,
        description: form.description,
        image: form.image,
        price: form.price === "" ? null : Number(form.price),
        fat: form.fat,
        difficulty: form.difficulty,
        tags,
        flavor: Number(form.flavor),
        tenderness: Number(form.tenderness),
        fresh_cut: form.fresh_cut,
        featured: form.featured,
        is_premium: form.is_premium,
      };
      if (isCreate) {
        const methods = form.cooking_methods.split(",").map((s) => s.trim()).filter(Boolean);
        await adminCreateProduct({
          slug: form.slug.trim(),
          cooking_methods: methods.length ? methods : ["Tava"],
          ...base,
        });
        toast.success("Ürün oluşturuldu");
      } else {
        await adminUpdateProduct(p.slug, base);
        toast.success("Ürün güncellendi");
      }
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            {isCreate ? "Yeni Ürün" : `Düzenle · ${p.name}`}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-2 sm:grid-cols-2">
          {isCreate && (
            <Field label="Slug (benzersiz, küçük harf)">
              <Input
                data-testid="product-slug-input"
                value={form.slug}
                onChange={(e) => set("slug", e.target.value)}
                placeholder="ornek-urun"
              />
            </Field>
          )}
          <Field label="Ürün Adı">
            <Input
              data-testid="product-name-input"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
            />
          </Field>
          <Field label="Kategori">
            <select className={selectCls} value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="product-category-select">
              {categoryNames.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Fiyat (₺/kg)">
            <Input
              data-testid="product-price-input"
              type="number"
              value={form.price}
              onChange={(e) => set("price", e.target.value)}
            />
          </Field>
          <Field label="Zorluk">
            <select className={selectCls} value={form.difficulty} onChange={(e) => set("difficulty", e.target.value)}>
              {["Kolay", "Orta", "Usta işi"].map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Yağ Oranı">
            <select className={selectCls} value={form.fat} onChange={(e) => set("fat", e.target.value)}>
              {["Az", "Orta", "Yüksek"].map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Ürün Görseli (sunucuya yüklenir)" full>
            <div className="flex items-center gap-3">
              {form.image ? (
                <img
                  src={form.image}
                  alt="önizleme"
                  data-testid="product-image-preview"
                  className="h-16 w-16 flex-shrink-0 rounded-sm border border-charcoal/15 object-cover"
                />
              ) : (
                <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-sm border border-dashed border-charcoal/25 text-charcoal/30">
                  <ImageIcon className="h-5 w-5" />
                </div>
              )}
              <div>
                <input
                  id="product-image-file"
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={handleImageUpload}
                  data-testid="product-image-file"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={uploading}
                  onClick={() => document.getElementById("product-image-file").click()}
                  data-testid="product-image-upload-btn"
                >
                  {uploading ? "Yükleniyor…" : form.image ? "Görseli Değiştir" : "Görsel Yükle"}
                </Button>
                <p className="mt-1 font-mono text-[10px] text-charcoal/40">JPG / PNG / WebP · maks. 5MB · sunucuda saklanır</p>
              </div>
            </div>
          </Field>
          <Field label="Kısa Açıklama" full>
            <Textarea value={form.short_description} onChange={(e) => set("short_description", e.target.value)} rows={2} />
          </Field>
          {isCreate && (
            <Field label="Detaylı Açıklama" full>
              <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} />
            </Field>
          )}
          {isCreate && (
            <Field label="Pişirme Yöntemleri (virgülle)" full>
              <Input
                value={form.cooking_methods}
                onChange={(e) => set("cooking_methods", e.target.value)}
                placeholder="Mangal, Tava"
              />
            </Field>
          )}
          <Field label="Etiketler (virgülle)" full>
            <Input
              value={form.tags}
              onChange={(e) => set("tags", e.target.value)}
              placeholder="Mangal Favorisi, Şef Önerisi"
            />
          </Field>
          <div className="col-span-full flex flex-wrap gap-6 pt-1">
            <Toggle label="Bugün Taze Kesim" v={form.fresh_cut} on={(v) => set("fresh_cut", v)} testid="dialog-fresh-toggle" />
            <Toggle label="Öne Çıkan" v={form.featured} on={(v) => set("featured", v)} testid="dialog-featured-toggle" />
            <Toggle label="Premium" v={form.is_premium} on={(v) => set("is_premium", v)} testid="dialog-premium-toggle" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            İptal
          </Button>
          <Button
            data-testid="product-save-btn"
            onClick={save}
            disabled={saving}
            className="bg-bordo text-cream hover:bg-bordo-dark"
          >
            {saving ? "Kaydediliyor…" : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
